import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { UserPlus, Send, UserX, ShieldCheck, Users as UsersIcon, Dumbbell } from 'lucide-react';
import type { StaffMember, UserStatus } from '@techsofit/api-types';
import { useAuth, ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Select, Avatar, Modal, EmptyState, Skeleton, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { ActionMenu, type ActionItem } from '../components/ActionMenu';
import { useAsync } from '../lib/useAsync';
import { getStaff, inviteStaff, updateStaff, resendStaffInvite, disableStaff } from '../lib/gymApi';
import { useGymData } from '../data/GymDataProvider';

export function StaffPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const { reloadCoaches, subscription } = useGymData();
  const isOwner = user?.role === 'GYM_OWNER';
  const currentEmail = (user?.email ?? '').toLowerCase();

  const { data, loading, error, reload } = useAsync(getStaff);
  const [inviteOpen, setInviteOpen] = useState(false);

  // Staff management is an owner-only module — keep it out of staff hands.
  if (!isOwner) return <Navigate to="/dashboard" replace />;

  const staff = data ?? [];
  const staffCap = subscription?.staffCap ?? null;
  const staffUsed = subscription?.staffCount ?? staff.length;
  const atCap = staffCap != null && staffUsed >= staffCap;

  async function act(fn: () => Promise<unknown>) {
    await fn();
    reload();
  }

  async function toggleCoach(member: StaffMember) {
    await updateStaff(member.id, { coach: !member.coach });
    reload();
    reloadCoaches(); // keep member/payment coach pickers in sync
  }

  function actionsFor(member: StaffMember): ActionItem[] {
    const items: ActionItem[] = [];
    if (member.status === 'INVITED') {
      items.push({
        label: 'Resend invite',
        icon: <Send className="size-4" aria-hidden />,
        onClick: () => act(() => resendStaffInvite(member.id)),
      });
    }
    if (member.status !== 'DISABLED') {
      items.push({
        label: member.coach ? 'Remove coach role' : 'Make coach',
        icon: <Dumbbell className="size-4" aria-hidden />,
        onClick: () => toggleCoach(member),
      });
    }
    if (member.role !== 'GYM_OWNER' && member.status !== 'DISABLED' && member.email.toLowerCase() !== currentEmail) {
      items.push({
        label: 'Disable access',
        icon: <UserX className="size-4" aria-hidden />,
        danger: true,
        onClick: async () => {
          const ok = await confirm({
            title: `Disable ${member.fullName}?`,
            description: 'They’ll lose access immediately.',
            confirmLabel: 'Disable',
            tone: 'danger',
          });
          if (ok) act(() => disableStaff(member.id));
        },
      });
    }
    return items;
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={staffCap != null ? `${staffUsed} of ${staffCap} staff` : `${staff.length} ${staff.length === 1 ? 'person' : 'people'}`}
        title="Staff"
        description="Invite front-desk staff and coaches, then manage who can access your business."
        actions={
          <div className="flex flex-col items-end gap-1">
            <Button onClick={() => setInviteOpen(true)} disabled={atCap}>
              <UserPlus className="size-4" aria-hidden />
              <span className="hidden sm:inline">Invite staff</span>
            </Button>
            {atCap && (
              <span className="text-xs text-muted">
                Staff limit reached ·{' '}
                <Link to="/billing" className="font-medium text-ink underline">Upgrade</Link>
              </span>
            )}
          </div>
        }
      />

      <Card className="overflow-hidden p-0">
        {/* Desktop header row */}
        <div className="hidden grid-cols-[1.5fr_1fr_auto] gap-4 border-b border-line px-5 py-3 text-eyebrow uppercase text-muted md:grid">
          <span>Name</span>
          <span>Status</span>
          <span className="text-right">&nbsp;</span>
        </div>

        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon={<UsersIcon className="size-6" aria-hidden />}
            title="Couldn’t load staff"
            description={error}
            className="rounded-none shadow-none"
          />
        ) : staff.length ? (
          <ul className="divide-y divide-line">
            {staff.map((member) => (
              <StaffRow key={member.id} member={member} actions={actionsFor(member)} />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<UsersIcon className="size-6" aria-hidden />}
            title="No staff yet"
            description="Invite a front-desk teammate or coach to share the load."
            className="rounded-none shadow-none"
            action={
              <Button onClick={() => setInviteOpen(true)}>
                <UserPlus className="size-4" aria-hidden /> Invite staff
              </Button>
            }
          />
        )}
      </Card>

      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite staff"
        description="They’ll get an email to set a password and join your business."
      >
        <InviteForm onCancel={() => setInviteOpen(false)} onInvited={() => { setInviteOpen(false); reload(); }} />
      </Modal>
    </section>
  );
}

function StaffRow({ member, actions }: { member: StaffMember; actions: ActionItem[] }) {
  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 md:grid-cols-[1.5fr_1fr_auto] md:px-5">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar name={member.fullName} size="sm" />
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">
            {member.fullName}
            {member.role === 'GYM_OWNER' && <span className="ml-1.5 text-xs font-normal text-muted">· Owner</span>}
            {member.coach && (
              <span className="ml-1.5 inline-flex items-center gap-1 rounded-pill bg-surface px-2 py-0.5 align-middle text-xs font-medium text-ink-soft">
                <Dumbbell className="size-3" aria-hidden /> Coach
              </span>
            )}
          </p>
          <p className="truncate text-sm text-muted">{member.email}</p>
          {/* Status sits under the name on phones, where there's no dedicated column. */}
          <div className="mt-1.5 md:hidden">
            <StaffStatus status={member.status} lastLoginAt={member.lastLoginAt} />
          </div>
        </div>
      </div>

      <span className="hidden md:block">
        <StaffStatus status={member.status} lastLoginAt={member.lastLoginAt} />
      </span>

      <div className="flex justify-end">
        {actions.length > 0 ? (
          <ActionMenu items={actions} label={`Actions for ${member.fullName}`} />
        ) : (
          <span className="inline-block size-9" aria-hidden />
        )}
      </div>
    </li>
  );
}

function StaffStatus({ status, lastLoginAt }: { status: UserStatus; lastLoginAt?: string | null }) {
  const map: Record<UserStatus, { label: string; cls: string; icon?: boolean }> = {
    ACTIVE: { label: 'Active', cls: 'bg-ok-soft text-ok-ink', icon: true },
    INVITED: { label: 'Invited', cls: 'bg-warn-soft text-warn-ink' },
    DISABLED: { label: 'Disabled', cls: 'bg-neutral-soft text-neutral-ink' },
  };
  const s = map[status] ?? map.INVITED;
  const hint =
    status === 'DISABLED'
      ? null
      : lastLoginAt
        ? `Last in ${new Date(lastLoginAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`
        : 'Hasn’t signed in yet';
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className={cn('inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-xs font-medium', s.cls)}>
        {s.icon && <ShieldCheck className="size-3" aria-hidden />}
        {s.label}
      </span>
      {hint && <span className="text-[11px] text-muted">{hint}</span>}
    </span>
  );
}

function InviteForm({ onCancel, onInvited }: { onCancel: () => void; onInvited: () => void }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [coach, setCoach] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!fullName.trim() || !email.trim()) {
      setError('Enter a name and email.');
      return;
    }
    setError(null);
    setInviting(true);
    try {
      await inviteStaff({ fullName: fullName.trim(), email: email.trim(), coach });
      onInvited();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not send the invite.');
      setInviting(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Input label="Name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Front desk name" autoFocus />
      <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" />

      <Select label="Role" value={coach ? 'COACH' : 'STAFF'} onChange={(e) => setCoach(e.target.value === 'COACH')}>
        <option value="STAFF">Staff</option>
        <option value="COACH">Coach</option>
      </Select>

      {error && (
        <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={inviting}>
          Cancel
        </Button>
        <Button type="submit" loading={inviting}>
          <UserPlus className="size-4" aria-hidden /> Send invite
        </Button>
      </div>
    </form>
  );
}
